<script setup lang="ts">
import { useStepper } from "@vueuse/core";
import useAsync from "~/hooks/use-async";
import BillingAPI from "~/services/billing";
import useEventStore from "~/stores/event";
import getErrorLocalePathMessage from "~/utils/locale";

const { t, te } = useI18n();
const route = useRoute();
const eventStore = useEventStore();
const redeemCode = ref("");
const commerceTitle = ref<string | null>(null);

const stepper = useStepper({
  "input-reedem-code": {
    title: computed(() => t("pages.account_subscription.redeem_access_code_title")),
    errorMessage: "",
  },
  "success-code-applied": {
    title: "",
    errorMessage: "",
  },
  "failure-code-applied": {
    title: "",
    errorMessage: computed(() => t("pages.events.failed_code_applied_title")),
  },
  "failure-code-already-used": {
    title: "",
    errorMessage: computed(() => t("pages.events.failed_already_used_code")),
  },
});

const onSuccess = (commerce: IModelCommerce) => {
  commerceTitle.value = commerce.title;
  stepper.goTo("success-code-applied");
};

const onError = (error: Error) => {
  const { code } = getErrorLocalePathMessage(error.message, { t, te });

  if (code === "ERR409CM11") // Already used
    stepper.goTo("failure-code-already-used");
  else
    stepper.goTo("failure-code-applied");
};

const { loading, run: onAccessCodeRedeem } = useAsync(BillingAPI.redeemAccessCode, { onSuccess, onError });

const onClickSubmit = (code: string) => {
  redeemCode.value = code;
  onAccessCodeRedeem(code);
};

const onClickClose = async () => {
  if (stepper.isCurrent("success-code-applied")) {
    await eventStore.load(route.params.eventId as string);
    eventStore.canReloadSuggested = true;
  }
  stepper.goTo("input-reedem-code");
};

const onClickTryAgain = () => {
  onAccessCodeRedeem(redeemCode.value);
};

const onClickGoToEvent = async (callBack: () => void) => {
  await eventStore.load(route.params.eventId as string);
  eventStore.canReloadSuggested = true;
  eventStore.isEventStartingSoon = false;
  callBack();
};
</script>

<template>
  <BaseModal
    :title="stepper.current.value.title" root-class="max-w-[30rem]" :no-back-drop-click="loading"
    @close="onClickClose"
  >
    <template #before-title>
      <slot name="before-title" />
    </template>
    <template #trigger="{ openModal }">
      <slot name="trigger" :open-modal="openModal" />
    </template>
    <template #actions="{ closeModal }">
      <CommerceAccessCodeRedeem
        v-if="stepper.isCurrent('input-reedem-code')"
        root-class="mt-5"
        :disabled="loading"
        outline
        :input-placeholder="t('pages.events.submit_reedem_code_input_placeholder')"
        @submit="onClickSubmit"
      >
        <template #button="{ disabled, onSubmit }">
          <BaseButton
            class="mt-6 font-bold" full-width name="submit-redeem" type="primary" :disabled="disabled"
            @click.prevent="onSubmit()"
          >
            {{ t('button.submit_code') }}
          </BaseButton>
        </template>
      </CommerceAccessCodeRedeem>

      <div v-if="stepper.isCurrent('success-code-applied')" class="flex flex-col w-full justify-center items-center">
        <div class="w-[64px] h-[64px] flex justify-center items-center rounded-full bg-[var(--base-success-color)]">
          <IconCheck :width="24" :height="24" fill="#ffff" />
        </div>
        <div class="my-6 flex flex-col justify-center items-center">
          <span class="text-main font-bold text-2xl">{{ t('pages.events.appreciation') }}</span>
          <span class="text-base mt-4 text-secondary">
            {{ t('pages.events.success_code_applied', { title: commerceTitle }) }}
          </span>
        </div>
        <BaseButton
          name="redirectx-to-event" class="font-bold text-white text-xs sm:text-base"
          type="primary" @click="onClickGoToEvent(closeModal)"
        >
          {{ t('labels.go_to_event') }}
        </BaseButton>
      </div>

      <div
        v-if="stepper.isCurrent('failure-code-already-used')
          || stepper.isCurrent('failure-code-applied')" class="flex flex-col w-full justify-center items-center"
      >
        <div class="w-[64px] h-[64px] flex justify-center items-center rounded-full bg-[var(--base-error-color)]">
          <IconClose :width="24" :height="24" fill="#ffff" />
        </div>
        <div class="my-6 flex flex-col justify-center items-center">
          <span class="text-main font-bold text-2xl">
            {{ stepper.current.value.errorMessage }}
          </span>
          <span class="text-base mt-4 text-secondary">
            {{ t('pages.events.failed_code_applied_message') }}
          </span>
        </div>
        <div class="flex gap-4 max-w-[25rem]">
          <BaseButton
            name="enter-another-card" type="secondary" class="font-bold text-white truncate text-xs sm:text-base"
            :title="t('button.enter_another_code')" :disabled="loading"
            @click="stepper.goTo('input-reedem-code')"
          >
            {{ t('button.enter_another_code') }}
          </BaseButton>
          <BaseButton
            name="try-again" class="font-bold text-white text-xs truncate sm:text-base min-w-[10rem]"
            type="primary" :title="t('button.try_again')"
            :disabled="loading" @click="onClickTryAgain"
          >
            {{ t('button.try_again') }}
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>
